import AppButton from "components/AppButton/AppButton";
import ContainerFlex from "components/ContainerFlex";
import TableAntecipation from "components/TableAntecipation";
import useApi, { ApiMethod } from "hooks/useApi";
import moment from "moment";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/app/hooks";
import {
  addValueAntecipationPurchase,
  addValueDiscountAntecipationPurchase,
  addValuePaymentInvoice,
  clienteSlices,
} from "redux/features/cliente/cliente-slice";
import { addTypeFlowState } from "redux/features/security/security-slice";
import type { AntecipationType } from "models/cliente";
import { setLoading, setTitleLoading } from "redux/ui/loadings-slice";
import { RouteNames } from "routes/routes";
import Endpoints from "services/api/endpoints";
import { currencyMask, hiddenCardNumber, hideCpf, moneyMask } from "utils/mask";
import { showMessage } from "utils/messages";
import {
  AntecipationPurchaseResponse,
  IAntecipationPurchase,
} from "./interfaces/AntecipationPurchase";
import * as St from "./styles";

const AntecipationPurchase = () => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [totalCompra, setTotalCompra] = useState<number>(0);
  const [totalDesconto, setTotalDesconto] = useState<number>(0);
  const [list, setList] = useState<IAntecipationPurchase[]>([]);
  const [rawList, setRawList] = useState<AntecipationPurchaseResponse[]>([]);

  const {
    cliente: { cpf },
  } = useAppSelector((state) => state.cliente);
  const dispatch = useAppDispatch();

  const history = useHistory();

  const { setAntecipationList } = clienteSlices.actions;

  const getAntecipationsPurchase = useApi<{
    mensagem: string;
    data: AntecipationPurchaseResponse[] | { mensagem: string };
  }>(
    Endpoints.cobranca.getAntecipationPurchaseSearch.replace(
      "PARAM_CPF",
      cpf || ""
    ),
    ApiMethod.GET
  );

  const headerTable = [
    "Nome Completo",
    "CPF",
    "Número do Cartão",
    "Data da Compra",
    "Valor da Compra",
    "Valor Pendente",
    "Quantidade de Parcelas Pendentes",
  ];

  const getAntecipations = async () => {
    dispatch(setLoading(true));
    dispatch(setTitleLoading("Carregando as antecipações, aguarde..."));

    const response = await getAntecipationsPurchase.callApi();
    const data = response?.data?.data;

    if (response?.status >= 200 && response?.status < 300) {
      if (Array.isArray(data)) {
        const filteredData = data.filter(
          (item) => item.qtdParcelasAPagar !== 0
        );

        const newList = filteredData?.map((item) => ({
          idCompra: item?.idCompra,
          nomeCompleto: item?.nomeCompleto,
          cpf: hideCpf(item?.cpf),
          numeroCartao: hiddenCardNumber(item.cartao),
          dataCompra: moment(item.dataCompra).format("DD/MM/YYYY"),
          valorCompra: moneyMask(item?.valorCompra),
          valorPendente: moneyMask(item?.valorPendente),
          qtdParcelasAPagar: String(item?.qtdParcelasAPagar ?? ""),
        }));
        setRawList(filteredData);

        setList(newList);
      } else if (rawList.length <= 0) {
        console.warn("Nenhum item:", data);
        showMessage(
          "warning",
          "Atenção!",
          "Antecipação não permitida! Favor realizar o pagamento da fatura disponível"
        );
        setList([]);
        setRawList([]);
        history.push(RouteNames.ATENDIMENTO_HOME);
      } else {
        console.warn("Resposta não esperada:", data);
        showMessage(
          "warning",
          "Atenção!",
          "Dados recebidos estão em formato inesperado."
        );
        setList([]);
        setRawList([]);
        history.push(RouteNames.ATENDIMENTO_HOME);
      }
    } else {
      const mensagem =
        data && !Array.isArray(data) && data.mensagem
          ? data.mensagem
          : "Não foi possível carregar as antecipações.";
      showMessage("warning", "Atenção!", mensagem);
      history.push(RouteNames.ATENDIMENTO_HOME);
    }

    dispatch(setLoading(false));
  };

  useEffect(() => {
    getAntecipations();
    dispatch(addTypeFlowState("antecipacao"));

    if (!list && !cpf) {
      showMessage("warning", "Atenção!", "Nenhuma antecipação encontrada!");
      history.push(RouteNames.ATENDIMENTO_HOME);
    }
    // getAntecipations vem de useApi/e nao e memoizada; e "list" e alterado pela
    // propria getAntecipations (setList) — incluir qualquer um dos dois causaria
    // refetch em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpf, dispatch, history]);

  const clickVoltar = () => {};

  const handleButton = () => {
    history.push({
      pathname: RouteNames.ESCOLHA_PAGAMENTO,
      state: { totalCompra, antecipation: true },
    });
  };

  const handleHistory = () => {
    dispatch(addTypeFlowState("antecipacao"));
    history.push({
      pathname: RouteNames.ANTECIPACAO_HISTORICO,
    });
  };

  const parseMoney = (value: string | number): number => {
    if (typeof value === "number") return value;

    if (typeof value !== "string") return 0;

    try {
      const numericString = value
        .replace("R$", "")
        .replace(/\s/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      return parseFloat(numericString);
    } catch (error) {
      console.error("Erro ao formatar valor monetário:", value);
      return 0;
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedItems((prev) => {
      const isSelected = prev.includes(id);
      const newSelected = isSelected
        ? prev.filter((v) => v !== id)
        : [...prev, id];

      const newSelectedObjects =
        rawList?.filter((item) => newSelected.includes(item?.idCompra || "")) ||
        [];

      const totalSemDesconto = newSelectedObjects.reduce(
        (sum: number, item: AntecipationPurchaseResponse) => {
          const rawValue = item?.valorPendente || "R$ 0,00";
          const parsed = parseMoney(rawValue);

          if (isNaN(parsed)) {
            console.warn("Valor inválido encontrado:", rawValue);
            return sum;
          }
          return sum + parsed;
        },
        0
      );

      const total = newSelectedObjects.reduce(
        (sum: number, item: AntecipationPurchaseResponse) => {
          const rawValue = item?.valorTotalCobrar || "R$ 0,00";
          const parsed = parseMoney(rawValue);

          if (isNaN(parsed)) {
            console.warn("Valor inválido encontrado:", rawValue);
            return sum;
          }
          return sum + parsed;
        },
        0
      );

      const totalDesconto = newSelectedObjects.reduce(
        (sum: number, item: AntecipationPurchaseResponse) => {
          const rawValue = item?.desconto || 0;
          const parsed = parseMoney(rawValue);

          if (isNaN(parsed)) {
            console.warn("Valor inválido encontrado:", rawValue);
            return sum;
          }
          return sum + parsed;
        },
        0
      );

      setTotalCompra(total);
      setTotalDesconto(totalDesconto);
      // AntecipationPurchaseResponse (contrato da API) e AntecipationType (store)
      // modelam a mesma antecipacao em camadas diferentes e nao sao estruturalmente
      // identicos (varios campos numericos vs string) — cast pontual na fronteira.
      dispatch(setAntecipationList(newSelectedObjects as unknown as AntecipationType[]));
      dispatch(addValueAntecipationPurchase(total));
      dispatch(addValueDiscountAntecipationPurchase(totalDesconto));
      dispatch(addValuePaymentInvoice(String(totalSemDesconto)));
      return newSelected;
    });
  };

  const toggleAll = (data: IAntecipationPurchase[]) => {
    if (selectedItems?.length === data?.length) {
      setSelectedItems([]);
      setTotalCompra(0);
    } else {
      const allIds = data
        .map((item) => item.idCompra)
        .filter((id): id is string => typeof id === "string");

      const totalSemDesconto = rawList?.reduce((sum, item) => {
        const parsed = parseMoney(item?.valorPendente || 0);
        return sum + (isNaN(parsed) ? 0 : parsed);
      }, 0);

      const total = rawList?.reduce((sum, item) => {
        const parsed = parseMoney(item?.valorTotalCobrar || 0);
        return sum + (isNaN(parsed) ? 0 : parsed);
      }, 0);

      const totalDesconto = rawList?.reduce(
        (sum: number, item: AntecipationPurchaseResponse) => {
          const rawValue = item?.desconto || 0;
          const parsed = parseMoney(rawValue);

          if (isNaN(parsed)) {
            console.warn("Valor inválido encontrado:", rawValue);
            return sum;
          }
          return sum + parsed;
        },
        0
      );

      setSelectedItems(allIds);
      setTotalCompra(total);
      setTotalDesconto(totalDesconto);

      dispatch(setAntecipationList(rawList as unknown as AntecipationType[]));
      dispatch(addValueAntecipationPurchase(total));
      dispatch(addValueDiscountAntecipationPurchase(totalDesconto));
      dispatch(addValuePaymentInvoice(String(totalSemDesconto)));

      return allIds;
    }
  };

  return (
    <ContainerFlex>
      <St.TitleH1>Antecipação de compras</St.TitleH1>
      <St.TableScroll>
        <TableAntecipation
          header={headerTable}
          data={list}
          selectedItems={selectedItems}
          onToggleItem={toggleSelection}
          onToggleAll={toggleAll}
          selectAll
        />
      </St.TableScroll>
      <St.FooterFixed>
        <ContainerFlex row align="flex-start">
          <ContainerFlex row gap="10px">
            <AppButton
              content="Voltar"
              route={RouteNames.ATENDIMENTO_HOME}
              outlined={true}
              click={clickVoltar}
            />
            <AppButton
              content="Ultimas transações"
              outlined={true}
              click={handleHistory}
              route={RouteNames.ANTECIPACAO_HISTORICO}
            />
            {selectedItems.length > 0 && (
              <AppButton
                content={
                  <>
                    <span>Pagar: R$ {currencyMask(totalCompra)}</span>
                    {totalDesconto > 0 && (
                      <span style={{ fontSize: "18px" }}>
                        Desconto: R$ {currencyMask(totalDesconto)}
                      </span>
                    )}
                  </>
                }
                labelDirection="column"
                click={handleButton}
                hidden={false}
              ></AppButton>
            )}
          </ContainerFlex>
        </ContainerFlex>
      </St.FooterFixed>
    </ContainerFlex>
  );
};

export default AntecipationPurchase;
